import { Color, Spacing } from '@persona-id/design-tokens';
import { EditIcon } from '@persona-id/design-tokens/icons';
import { Fragment, useMemo, VFC } from 'react';
import styled from 'styled-components';

import HandlebarFormControl from 'dashboard/components/HandlebarFormControl';
import { OptionType, ReactCreatableSelect } from 'dashboard/components/ReactSelect';
import ParamLabel from 'dashboard/components/WorkflowControlPanel/ParamLabel';
import EditKeyValuePairsPanel from 'dashboard/components/WorkflowControlPanel/StepConfigAction/EditKeyValuePairsPanel';
import MaximizablePanel from 'dashboard/components-lib/MaximizablePanel';
import { generateJsonSchemaConfig, UsableObjectConfig } from 'dashboard/lib/workflows';
import { WorkflowActionHubspotUpdateObjectV1 } from 'dashboard/models/workflow-version';
import Button from 'design-system/components/Button';
import Input from 'design-system/components/Input';
import Spacer from 'design-system/components/Spacer';
import { HStack } from 'design-system/components/Stack';
import { BASE } from 'design-system/variables/spacing';
import { FontWeight } from 'design-system/variables/text';
import usePermission from 'lib/hooks/usePermission';
import useToggle from 'lib/hooks/useToggle';
import { JsonSchemaTypes } from 'lib/json-schema';
import { Permission } from 'lib/permissions';

const InputFieldsHeading = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
`;

const HorizontalScrollableBox = styled.div`
  font-family: ${({ theme }) => theme.text.fontFamily.mono};
  font-size: ${({ theme }) => theme.text.paragraph.md.fontSize};
  font-weight: ${FontWeight.Medium};
  padding: ${Spacing[4]};
  background-color: ${Color.CoreSlate50};
  border-radius: ${({ theme }) => theme.element.borderRadius};
  overflow-x: scroll;
`;

const Row = styled.div`
  display: flex;
  padding: ${BASE / 4}px;
  flex-direction: row;
`;

const Key = styled.div`
  font-weight: ${FontWeight.Bold};
  white-space: nowrap;
`;

const Value = styled.div`
  white-space: nowrap;
  margin-left: ${Spacing[2]};
  padding-right: ${Spacing[4]}; // Extra space when scrolling to right edge
`;

interface ParamsHubspotUpdateObjectV1Props {
  editable: boolean;
  params: WorkflowActionHubspotUpdateObjectV1['params'];
  usableObjects: UsableObjectConfig[];
  updateActionParams: (newParams: WorkflowActionHubspotUpdateObjectV1['params']) => void;
}

const supportedObjectType: Record<
  WorkflowActionHubspotUpdateObjectV1['params']['object-type'],
  string
> = {
  contacts: 'Contact',
  companies: 'Company',
  deals: 'Deal',
};

const ParamsHubspotUpdateObjectV1: VFC<ParamsHubspotUpdateObjectV1Props> = ({
  editable,
  params,
  usableObjects,
  updateActionParams,
}) => {
  const { jsonSchema, tokensToDisplayValue } = useMemo(() => {
    return generateJsonSchemaConfig({
      usableObjects,
    });
  }, [usableObjects]);

  const canViewApiKey = usePermission(Permission.ApiKeyView);

  if (!(params['object-type'] in supportedObjectType)) {
    supportedObjectType[params['object-type']] = params['object-type'];
  }

  const statusOptions: OptionType[] = Object.entries(supportedObjectType).map(([value, label]) => ({
    value,
    label,
  }));

  const updateHubspotObjectParams = <
    Key extends keyof WorkflowActionHubspotUpdateObjectV1['params'],
  >(
    key: Key,
    value: WorkflowActionHubspotUpdateObjectV1['params'][Key],
  ) => {
    updateActionParams({
      ...params,
      [key]: value,
    });
  };

  const [showEditPanel, { disable: closeEditPanel, toggle: toggleEditPanel }] = useToggle(false);

  const keyValueObject: Record<string, string> = JSON.parse(params['object-properties']);

  return (
    <>
      <Spacer height={2} />
      <ParamLabel labelText='Access Token' htmlFor='params-hubspot-update-object__access-token' />
      <Input
        value={canViewApiKey ? params['access-token'] : 'Key Hidden'}
        onChange={(e) => updateHubspotObjectParams('access-token', e.target.value)}
        disabled={!editable || !canViewApiKey}
        id='params-hubspot-update-object__access-token'
      />
      <Spacer height={2} />
      <ParamLabel
        labelText='Object Type'
        htmlFor='params-hubspot-update-object__object-type-input'
      />
      <ReactCreatableSelect
        value={params['object-type']}
        options={statusOptions}
        onChange={(value: WorkflowActionHubspotUpdateObjectV1['params']['object-type']) => {
          updateHubspotObjectParams('object-type', value);
        }}
        onCreateOption={(str: string) => {
          updateHubspotObjectParams('object-type', str);
        }}
        id='params-hubspot-update-object__object-type'
        disabled={!editable}
      />
      <Spacer height={2} />
      <ParamLabel labelText='Object Id' htmlFor='params-hubspot-update-object__object-id' />
      <HandlebarFormControl
        size='md'
        type='input'
        value={params['object-id']}
        updateValue={(value: string) => {
          updateHubspotObjectParams('object-id', value);
        }}
        editable={editable}
        allowedTypes={[JsonSchemaTypes.String]}
        jsonSchema={jsonSchema}
        tokensToDisplayValue={tokensToDisplayValue}
        id='params-hubspot-update-object__object-id'
      />
      <Spacer height={2} />
      <InputFieldsHeading>
        <ParamLabel
          labelText=' Object Properties'
          htmlFor='params-hubspot-update-object__object-properties'
        />
        <HStack>
          <Button
            width='icon'
            variant='basic'
            onClick={() => toggleEditPanel()}
            id='params-hubspot-update-object__object-properties'
            disabled={!editable}
          >
            <EditIcon />
          </Button>
        </HStack>
      </InputFieldsHeading>
      <Spacer height={2} />
      <HorizontalScrollableBox role='tree'>
        {Object.entries(keyValueObject).length === 0 && 'No Data'}
        {Object.entries(keyValueObject).length > 0 &&
          Object.entries(keyValueObject).map((keyValuePair, idx) => (
            <Fragment key={idx}>
              <Row>
                <Key>{keyValuePair[0]} : </Key>

                <Value>{keyValuePair[1]}</Value>
              </Row>
            </Fragment>
          ))}
      </HorizontalScrollableBox>
      {showEditPanel && (
        <MaximizablePanel
          outsideExceptionalIdPrefixes={['edit-key-value-panel__']}
          title='Properties Detail'
          onOutsideClickAndEscPress={closeEditPanel}
        >
          <EditKeyValuePairsPanel
            editable={editable}
            keyValueObject={keyValueObject}
            updateObject={(newProperty) =>
              updateHubspotObjectParams('object-properties', JSON.stringify(newProperty))
            }
            jsonSchema={jsonSchema}
            tokensToDisplayValue={tokensToDisplayValue}
          />
        </MaximizablePanel>
      )}
    </>
  );
};

export default ParamsHubspotUpdateObjectV1;
